import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  CouponStatus,
  CouponStatusColor,
} from 'src/app/shared/models/constants/coupon-status';
import { RewardType } from 'src/app/shared/models/constants/reward-type';
import { Coupon } from 'src/app/shared/models/dtos/responses/coupon/coupon';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  selector: 'app-coupon-card-slim',
  templateUrl: './coupon-card-slim.component.html',
  styleUrls: ['./coupon-card-slim.component.scss'],
})

/**
 * Coupon card slim alternative
 */
export class CouponCardSlimComponent implements OnInit, OnDestroy {
  @Input() coupon!: Coupon;
  activeStatus = CouponStatus.ACTIVE;
  redeemedStatus = CouponStatus.REDEEMED;
  canceledStatus = CouponStatus.CANCELED;
  expiredStatus = CouponStatus.EXPIRED;
  labels = {
    expirationDate: 'expiration-date',
    issuedDate: 'issued-date',
    copyCouponCode: 'copy-coupon-card-code',
    seeRewardsDetails: 'see-reward-details',
    reactivateCoupon: 'reactivate-coupon',
    requested: 'requested',
    active: 'active-coupon',
    redeemed: 'redeemed-coupon',
    expired: 'expired-coupon',
    disclaimer: 'disclaimer-coupon',
    canceled: 'canceled-coupon',
    error: 'backend-comm-error',
    codeCopied: 'code-copied',
  };
  RoutesKeys = RoutesKeys;
  private subsink = new Subscription();
  successfulReactivation: boolean = true;
  loading = false;
  cost!: number;
  language = '';
  hasDisclaimer = false;

  /**
   * @constructor
   */
  constructor(
    readonly translationService: TranslationService,
    private readonly router: Router,
    private readonly rewardService: RewardService,
    private clipboard: Clipboard,
    private readonly snackBarService: SnackBarService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.language = this.translationService.getActiveLang();
    this.translationService.translateObject(this.labels);
    this.cost = this.coupon?.rewardCost!;

    if (this.coupon.rewardType === RewardType.CUSTOMER_SERVICE_EXTENSION) {
      this.hasDisclaimer = true;
    }
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Get item color according to coupon status
   * @param {CouponStatus} status
   * @return {string}
   */
  getStatusColor(status?: CouponStatus): string {
    let color: string = '';
    switch (status) {
      case CouponStatus.ACTIVE:
        color = CouponStatusColor.GREEN;
        break;
      case CouponStatus.REDEEMED:
        color = CouponStatusColor.BLUE;
        break;
      case CouponStatus.EXPIRED:
        color = CouponStatusColor.GRAY;
        break;
      case CouponStatus.CANCELED:
        color = CouponStatusColor.RED;
        break;
    }
    return color;
  }

  /**
   * On route to reward details page
   */
  openRewardDetails() {
    this.router.navigate([RoutesKeys.REWARDS, this.coupon?.rewardId]);
  }

  /**
   * On route to coupon details page
   */
  openCouponDetails() {
    this.router.navigate([RoutesKeys.COUPONS, this.coupon?.id]);
  }

  /**
   * Copy text to clipboard
   */
  copy() {
    this.clipboard.copy(this.coupon.code as string);
    this.snackBarService.success(this.labels.codeCopied);
  }
}
